import { colorTokens } from '@kariusdx/design-system';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { getSession, signIn, signOut } from 'next-auth/react';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { EngagementEvents, identify, logKariusEvent } from '../src/Analytics';
import { logError, logWarn } from '../src/Analytics/AnalyticsClient';
import { checkIfTokenObjectValid } from '../src/api/ApiUtils';
import KariusLogoWidget from '../src/assets/images/svg/KariusLogoWidget';
import { isUserLoggedInAsLogicalApiToken, redirectByUserRole } from '../src/auth/accessUtils';
import { AppUser, UserRole } from '../src/auth/authTypes';
import { isOrderDetailDeepLink } from '../src/auth/getSessionTimeoutRedirectPath';
import LoginFooter from '../src/Footer/LoginFooter';
import LoginForm from '../src/Forms/Login/LoginForm';
import { LoginHelpSection } from '../src/Login/HelpAccordion';
import { parseLoginRedirect } from '../src/Login/loginRedirect';
import { MobileMarketing } from '../src/Login/MobileMarketing';
import { AccountBlocked } from '../src/Modal/AccountBlocked';
import { Auth0Expired } from '../src/Modal/Auth0Expired';
import { PasswordResetModal } from '../src/Modal/ResetPassword';
import { clearUserSessionStorage } from '../src/sessionStorage/utils';
import Colors from '../src/theme/colors';

const AUTH_ACTION_ERROR_PREFIX = 'Auth0 Action Error';
const LOGIN_ERROR_MESSAGE_FOR_CALLING_SUPPORT =
  'Failed to login, please call Karius at (866) 452-7487 for account assistance';

function Login() {
  const theme = useTheme();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [accountBlockedModalOpen, setAccountBlockedModalOpen] = useState(false);
  const [isBlockedExpiredRedirect, setIsBlockedExpiredRedirect] = useState(false);
  const [auth0ExpiredModalOpen, setAuth0ExpiredModalOpen] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [showUnblockSuccessAlert, setShowUnblockSuccessAlert] = useState(false);
  const [auth0ExpiredMessage, setAuth0ExpiredMessage] = useState<string | undefined>(undefined);
  const [modalFromUrlShowed, setModalFromUrlShowed] = useState(false);
  const [attempts, incrementAttempts] = useReducer((value) => value + 1, 1); // incorrect password sign in time

  const redirect = useMemo(() => parseLoginRedirect(router.query), [router.query]);
  const redirectMessage = redirect.kind === 'auth0LinkExpired' ? redirect.message : undefined;

  useEffect(() => {
    if (modalFromUrlShowed || !router.isReady) return;
    if (redirect.kind === 'none') return;

    setModalFromUrlShowed(true);
    switch (redirect.kind) {
      case 'accountBlockedExpired':
        setAccountBlockedModalOpen(true);
        setIsBlockedExpiredRedirect(true);
        logKariusEvent(EngagementEvents.account_locked_button_expired_shown);
        break;
      case 'auth0LinkExpired':
        setAuth0ExpiredModalOpen(true);
        setAuth0ExpiredMessage(redirectMessage);
        logKariusEvent(EngagementEvents.auth0_link_expired_shown);
        break;
      case 'unblockSuccess':
        setShowUnblockSuccessAlert(true);
        logKariusEvent(EngagementEvents.reset_password_or_unlock_successful, { type: 'locked_account' });
        break;
    }
  }, [modalFromUrlShowed, router.isReady, redirect.kind, redirectMessage]);

  useEffect(() => {
    async function checkUserRole() {
      const session = await getSession();
      const sessionUser: any = session?.user;
      if (sessionUser) {
        if (!!sessionUser?.access_token && !checkIfTokenObjectValid(sessionUser)) {
          await signOut(); // token exist but expired, need to sign out
        } else if (hasValidUserRole(sessionUser) && checkIfTokenObjectValid(sessionUser)) {
          await redirectByUserRole(sessionUser); // user has valid token, could directly sign in
        }
      }
    }

    checkUserRole();
  }, []);

  const onSubmitLogin = async (values: any, showLoginError: (message: any) => void) => {
    logKariusEvent(EngagementEvents.sign_in_tapped);
    setLoading(true);

    await signIn('credentials', {
      redirect: false,
      email: values.email,
      password: values.password,
    });

    const session = await getSession();
    const sessionUser: any = session?.user;
    // if errors sent from backend
    if (sessionUser && sessionUser?.error) {
      setLoading(false);
      if (!sessionUser.error_description || sessionUser.error_description === 'Wrong email or password.') {
        if (attempts === 5) {
          showLoginError('One more unsuccessful sign in attempt and your account will become locked.');
        } else if (attempts === 6) {
          // after 6 failed attempts account is auto locked
          showLoginError('Incorrect username or password.');
          setAccountBlockedModalOpen(true);
          logKariusEvent(EngagementEvents.account_locked_shown, {
            email_or_username: values.email,
          });
        } else {
          showLoginError('Incorrect username or password');
        }
        incrementAttempts();
      } else if (sessionUser.error === 'too_many_attempts') {
        setAccountBlockedModalOpen(true);
        logKariusEvent(EngagementEvents.account_locked_shown, {
          email_or_username: values.email,
        });
        showLoginError(
          'Your account is locked. Please check your email for a method to unlock your account, or you can reset your password.',
        );
      } else {
        if (sessionUser.error_description && sessionUser.error_description.startsWith(AUTH_ACTION_ERROR_PREFIX)) {
          logError('Unexpected login error', sessionUser);
          showLoginError(LOGIN_ERROR_MESSAGE_FOR_CALLING_SUPPORT);
        } else {
          logWarn('Login error displayed', sessionUser);
          showLoginError(sessionUser.error_description);
        }
      }
      return;
    }

    if (!sessionUser) {
      logError('Unexpected login issue, session user null');
      showLoginError(LOGIN_ERROR_MESSAGE_FOR_CALLING_SUPPORT);
      setLoading(false);
      return;
    }

    // if user role not found
    if (!hasValidUserRole(sessionUser)) {
      logError('No user role found', sessionUser);
      showLoginError('No user role found, please call Karius at (866) 452-7487 for account assistance');
      setLoading(false);
      return;
    }
    await identify(session);

    // On successful login, clear window.sessionStorage items
    // that are for a specific user since a new user has logged in
    clearUserSessionStorage();

    const hasMultipleInstitutions = Array.isArray(sessionUser.institutions) && sessionUser.institutions.length > 1;

    /* redirect to returnToPath after login, unless the user has multiple institutions and the
       path isn't an order detail deep link — then they must pick an institution first. */
    if (router?.query?.returnToPath) {
      const redirectParam = router.query.returnToPath;
      const redirectPath = Array.isArray(redirectParam) ? redirectParam[0] : redirectParam;
      try {
        const url = new URL(redirectPath, window.location.origin);
        if (url.origin === window.location.origin && (url.protocol === 'http:' || url.protocol === 'https:')) {
          const safeRedirectPath = `${url.pathname}${url.search}${url.hash}`;
          if (!hasMultipleInstitutions || isOrderDetailDeepLink(safeRedirectPath)) {
            await router.push(safeRedirectPath).catch((e) => {
              //if a custom redirect fails, log it, then redirect them instead to the default for their role.
              logError(`Error pushing to redirect path: ${safeRedirectPath}`, e);
            });
            return;
          }
        }
      } catch {
        // redirectPath is not a valid URL or is cross-origin, fall through to role-based redirect
      }
    }

    if (hasMultipleInstitutions) {
      await router.push('/institutions');
      return;
    }

    await redirectByUserRole(sessionUser);
  };

  function hasValidUserRole(sessionUser: AppUser) {
    if (!sessionUser) {
      return false;
    }

    if (isUserLoggedInAsLogicalApiToken(sessionUser)) {
      return true;
    }

    return !!(sessionUser.role && Object.values(UserRole).includes(sessionUser.role));
  }

  return (
    <>
      <Grid container justifyContent='center'>
        <Grid
          item
          container
          maxWidth='1100px'
          sx={{
            padding: {
              md: '60px 40px 91px',
              sm: '0px',
            },
            '& .link-text': {
              color: Colors.teal,
              fontWeight: 500,
            },
          }}
        >
          <Grid
            item
            container
            sx={{
              justifyContent: {
                md: 'flex-start',
                xs: 'center',
              },
              gap: theme.spacing(7),
              marginBottom: '21px',
            }}
            md={6}
            xs={12}
          >
            <Grid
              item
              container
              alignItems='center'
              flexDirection='column'
              sx={{
                backgroundColor: colorTokens.ui_05,
                boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
                maxWidth: {
                  md: '438px',
                },
                px: theme.spacing(5),
                py: theme.spacing(7),
              }}
            >
              <Box
                sx={{
                  maxWidth: '328px',
                }}
              >
                <KariusLogoWidget />
                <Typography
                  variant='heading_04'
                  component='div'
                  sx={{
                    mb: theme.spacing(7),
                    textAlign: 'center',
                  }}
                >
                  Portal
                </Typography>
              </Box>
              <LoginForm
                onSubmit={onSubmitLogin}
                loading={loading}
                onResetPasswordClick={() => setShowPasswordResetModal(true)}
                showUnblockSuccessAlert={showUnblockSuccessAlert}
              />
            </Grid>
            <LoginHelpSection />
          </Grid>
          <Grid
            item
            container
            sx={{
              justifyContent: {
                md: 'flex-end',
                xs: 'center',
              },
              boxShadow: {
                md: 'none',
                xs: '0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.2)',
              },
              padding: {
                md: '0px',
                xs: '17px 24px',
              },
            }}
            md={6}
            xs={12}
          >
            <MobileMarketing />
          </Grid>
        </Grid>
      </Grid>
      <LoginFooter />
      <AccountBlocked
        open={accountBlockedModalOpen}
        handleClose={() => {
          setAccountBlockedModalOpen(false);
          setIsBlockedExpiredRedirect(false);
        }}
        isRedirectFromBlockedExpiredPage={isBlockedExpiredRedirect}
        resetPassword={() => {
          setAccountBlockedModalOpen(false);
          setIsBlockedExpiredRedirect(false);
          setShowPasswordResetModal(true);
        }}
      />
      <Auth0Expired
        open={auth0ExpiredModalOpen}
        handleClose={() => setAuth0ExpiredModalOpen(false)}
        message={auth0ExpiredMessage}
      />
      <PasswordResetModal
        open={showPasswordResetModal}
        resetEmail={typeof router.query['email'] === 'string' ? router.query['email'] : null}
        handleClose={() => setShowPasswordResetModal(false)}
      />
    </>
  );
}

export default Login;
